import { useEffect, useState } from "react";

/**
 * Animated Gian-Luca avatar.
 *
 * Props:
 *  - size: "xs" | "sm" | "md" | "lg" | "xl"  (default "md") – for CIRCULAR mode
 *  - state: "idle" | "speaking" | "excited" | "wave"  (default "idle")
 *  - withGlow: boolean  (default false)  – soft violet halo behind the avatar
 *  - variant: "circle" | "fullbody"  (default "circle")
 *  - height: CSS height for fullbody (default "22rem")
 *  - onClick: optional click handler
 */
const SIZE_MAP = {
  xs: "w-8 h-8",
  sm: "w-10 h-10",
  md: "w-14 h-14",
  lg: "w-20 h-20",
  xl: "w-28 h-28",
};

const SRC = "/avatar/gianluca-cut.png?v=1";

export const GianlucaAvatar = ({
  size = "md",
  state = "idle",
  withGlow = false,
  variant = "circle",
  height = "22rem",
  className = "",
  onClick,
  testId,
}) => {
  const [bounce, setBounce] = useState(false);

  useEffect(() => {
    if (state !== "speaking") return;
    setBounce(true);
    const t = setTimeout(() => setBounce(false), 650);
    return () => clearTimeout(t);
  }, [state]);

  const animationClass =
    state === "excited"
      ? "gianluca-excited"
      : state === "wave"
      ? "gianluca-wave"
      : state === "speaking"
      ? bounce
        ? "gianluca-speaking"
        : "gianluca-float"
      : "gianluca-float";

  if (variant === "fullbody") {
    return (
      <div
        className={`relative inline-block pointer-events-none select-none ${className}`}
        style={{ height }}
        data-testid={testId || "gianluca-avatar-fullbody"}
        aria-label="Gian-Luca"
      >
        {withGlow && (
          <span
            className="absolute left-1/2 bottom-2 w-4/5 h-5 -translate-x-1/2 rounded-full bg-violet-500/30 blur-xl gianluca-glow"
            aria-hidden
          />
        )}
        <div className={`relative h-full ${animationClass}`}>
          <img
            src={SRC}
            alt="Gian-Luca"
            draggable={false}
            className="h-full w-auto object-contain drop-shadow-[0_20px_30px_rgba(0,0,0,0.5)]"
          />
        </div>
      </div>
    );
  }

  // --- Circle variant (default) ---
  const sizeClass = SIZE_MAP[size] || SIZE_MAP.md;

  // Render as <span> when not interactive to avoid <button>-in-<button>
  // hydration errors when nested inside other buttons.
  const Wrapper = onClick ? "button" : "span";
  const wrapperProps = onClick
    ? { type: "button", onClick, tabIndex: 0 }
    : { "aria-hidden": "true" };

  return (
    <Wrapper
      {...wrapperProps}
      className={`relative inline-flex items-center justify-center shrink-0 ${sizeClass} ${className} ${
        onClick ? "cursor-pointer" : "cursor-default"
      }`}
      aria-label="Gian-Luca"
      data-testid={testId || "gianluca-avatar"}
    >
      {withGlow && (
        <span
          className="absolute inset-0 rounded-full bg-gradient-to-br from-violet-500/40 via-fuchsia-400/25 to-cyan-400/30 blur-xl gianluca-glow"
          aria-hidden
        />
      )}
      <span
        className={`absolute inset-0 rounded-full overflow-hidden ring-2 ring-violet-400/40 shadow-[0_6px_20px_-6px_rgba(139,92,246,0.55)] bg-gradient-to-br from-violet-500/70 to-indigo-700/60 ${animationClass}`}
      >
        <img
          src={SRC}
          alt="Gian-Luca"
          draggable={false}
          style={{
            transform: "scale(2.6)",
            transformOrigin: "50% 10%",
          }}
          className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none"
        />
      </span>
      {(state === "speaking" || state === "excited") && (
        <span
          className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-slate-900 gianluca-pulse"
          aria-hidden
        />
      )}
    </Wrapper>
  );
};

export default GianlucaAvatar;
